import {Howl, Howler} from "howler";
import * as PIXI from "pixi.js";
import {config} from "../config";
import data from "./configs/data";
import {DeviceDetector} from "./DeviceDetector";
import {FullScreen} from "./FullScreen";
import {FunModeFeatures} from "./FunModeFeatures/FunModeFeatures";
import {InfoModal} from "./InfoModal";
import {LoadingScreen} from "./loadingScreen/LoadingScreen";
import {SessionInfoManager} from "./SessionInfoManager";
import {Utils} from "./Utils";
import {VideoPoker} from "./VideoPoker";

export class PixiApplication extends PIXI.Application {

    public static readonly WIDTH = 1280;
    public static readonly HEIGHT = 720;
    public static Resources: any;
    public static RENDERER: PIXI.SystemRenderer;

    public view: HTMLCanvasElement;

    private game: VideoPoker;
    private loadingScreen: LoadingScreen;
    private resources: any;
    private readonly SPINE_ATLAS_SUFFIX: string = "Atlas";

    constructor() {
        super(PixiApplication.WIDTH, PixiApplication.HEIGHT, {legacy: true});
        PixiApplication.RENDERER = this.renderer;
        document.getElementById("game").appendChild(this.view);
        document.getElementById("game").addEventListener("click", () => {
            if (DeviceDetector.isMobileOrTabletDevice()) {
                FullScreen.requestFullScreen();
                this.lockLandscapeMode();
            }
        });
        document.getElementById("game").addEventListener("touchend", () => {
            if (DeviceDetector.isMobileOrTabletDevice()) {
                FullScreen.requestFullScreen();
                this.lockLandscapeMode();
            }
        });

        PIXI.loader.add(data.loadingScreen.images.loadingScreenLogo)
            .add(data.loadingScreen.images.loadingBarBack)
            .add(data.loadingScreen.images.loadingBarCover)
            .load(() => {
                    this.createAndAddLoadingScreen();
                    this.loadGameAssets((loader: any, res: any) => {
                        if (DeviceDetector.isMobileOrTabletDevice()) {
                            this.createGame(loader, res);
                            this.loadSounds(() => {
                            });
                        } else {
                            this.loadSounds(this.createGame.bind(this, loader, res));
                        }
                    });
                },
            );

        for (const asset in data.fonts) {
            if (data.fonts.hasOwnProperty(asset)) {
                this.loadFont(asset, data.fonts[asset]);
            }
        }
    }

    public setCredentials(): void {
        const urlParams: any = Utils.getUrlVars();
        if (urlParams.steemAccountName) {
            SessionInfoManager.setSteemAccountName(urlParams.steemAccountName);
        }
        if (urlParams.steemConnectToken) {
            SessionInfoManager.setSteemConnectToken(urlParams.steemConnectToken);
        }
        if (urlParams.keyChainJWT) {
            SessionInfoManager.setKeychainJWT(urlParams.keyChainJWT);
        }

        if (urlParams.funMode) {
            FunModeFeatures.enableFunModeFeatures();
            SessionInfoManager.setSteemConnectToken("");
            SessionInfoManager.setKeychainJWT("");
        }

        if (!SessionInfoManager.getSteemAccountName() && !SessionInfoManager.getSteemConnectToken()) {
            InfoModal.open("Welcome back!", "You had a break so your session expired, but we saved all your results." +
                " \n Log in to go back to the game!", () => {
                if (top && top.window) {
                    top.window.location.replace(config.pageAddress);
                } else {
                    window.location.replace(config.pageAddress);
                }
            });
        } else {
            this.game.setCredentials(SessionInfoManager.getSteemAccountName(),
                SessionInfoManager.getSteemConnectToken(),
                SessionInfoManager.getKeychainJWT(),
            );
            this.game.initGame();
            if (!SessionInfoManager.getSteemConnectToken() && !SessionInfoManager.getKeychainJWT()) {
                InfoModal.open("Remember that you are not signed in.",
                    "You don't play for real money here, so you won't win any HIVE.");
            }
        }

        if (urlParams.forceLogin) {
            InfoModal.open("Hold your horses cowboy!", "First you have to login!",
                () => {
                    Utils.redirectToCasinoPage();
                });
            InfoModal.setActionButtonText("Login");
        }
    }

    private lockLandscapeMode(): void {
        (screen as any).orientation.lock("landscape").catch((err) => {
            if (err.name !== "NotSupportedError") {
                throw new Error(err);
            }
        });
    }

    private loadFont(fontFamily: string, source: string): void {
        const newStyle = document.createElement("style");
        const fontFace = "@font-face {font-family: '" + fontFamily + "'; src: url('" + source + "');}";
        newStyle.appendChild(document.createTextNode(fontFace));
        document.head.appendChild(newStyle);
        const newDiv = document.createElement("div");
        newDiv.textContent = fontFamily;
        newDiv.style.fontFamily = fontFamily;
        document.body.appendChild(newDiv);
    }

    private loadGameAssets(onAssetsLoaded: Function): void {
        this.privateLoadAssetsFromDataConfig(data.IMAGES_DATA_KEY);
        this.privateLoadAssetsFromDataConfig(data.SPRITE_SHEETS_DATA_KEY);
        this.privateLoadSpinesFromDataConfig();

        PIXI.loader.load(onAssetsLoaded);
        PIXI.loader.onProgress.add((loader) => {
            this.loadingScreen.updateProgressBarValue(loader.progress);
        });
    }

    private loadSounds(onSoundsLoaded: Function): void {
        const soundsArray = [];
        let sound;
        let soundsLoadedAmount = 0;
        for (const asset in data[data.AUDIOS_DATA_KEY]) {
            if (data[data.AUDIOS_DATA_KEY].hasOwnProperty(asset)) {
                sound = new Howl({
                    src: [data[data.AUDIOS_DATA_KEY][asset]],
                });
                sound.once("load", () => {
                    soundsLoadedAmount++;
                    if (soundsLoadedAmount === soundsArray.length) {
                        soundsArray.length = 0;
                        sound = undefined;
                        onSoundsLoaded();
                    }
                });
                soundsArray.push(sound);
            }
        }
    }

    private privateLoadAssetsFromDataConfig(assetName: string): void {
        for (const asset in data[assetName]) {
            if (data[assetName].hasOwnProperty(asset)) {
                PIXI.loader.add(asset, data[assetName][asset]);
            }
        }
    }

    private privateLoadSpinesFromDataConfig(): void {
        for (const asset in data[data.SPINE_ANIMATION_DATA_KEY]) {
            if (data[data.SPINE_ANIMATION_DATA_KEY].hasOwnProperty(asset)) {
                if (data[data.SPINE_ANIMATION_DATA_KEY].hasOwnProperty(asset + this.SPINE_ATLAS_SUFFIX)) {
                    const spineLoaderOptions = {
                        metadata: {
                            spineAtlasFile: data[data.SPINE_ANIMATION_DATA_KEY][asset + this.SPINE_ATLAS_SUFFIX],
                        },
                    };
                    PIXI.loader.add(asset, data[data.SPINE_ANIMATION_DATA_KEY][asset], spineLoaderOptions);
                } else if (!asset.includes(this.SPINE_ATLAS_SUFFIX)) {
                    PIXI.loader.add(asset, data[data.SPINE_ANIMATION_DATA_KEY][asset]);
                }
            }
        }
    }

    private createGame(loader: any, res: any): void {
        PixiApplication.Resources = res;
        this.resources = res;
        this.game = new VideoPoker();
        this.stage.removeChild(this.loadingScreen);
        this.stage.addChild(this.game);
        this.setCredentials();
    }

    private createAndAddLoadingScreen(): void {
        this.loadingScreen = new LoadingScreen();
        this.stage.addChild(this.loadingScreen);
    }
}



// WEBPACK FOOTER //
// ./src/app/PixiApplication.ts